import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Download, CheckCircle2, XCircle, AlertTriangle, Eye } from "lucide-react";
import { format } from "date-fns";

type EventType = "shown" | "accepted" | "dismissed" | "failed";
type Platform = "ios" | "android" | "other";

interface EventRow {
  id: string;
  event_type: EventType;
  platform: Platform | null;
  user_agent: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

const PERIODS = {
  "1d": { label: "Last 24h", days: 1 },
  "7d": { label: "Last 7 days", days: 7 },
  "30d": { label: "Last 30 days", days: 30 },
  "all": { label: "All time", days: null as number | null },
} as const;

type PeriodKey = keyof typeof PERIODS;

const InstallAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [period, setPeriod] = useState<PeriodKey>("7d");

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return navigate("/admin/login");
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);
      if (!roles?.some((r) => r.role === "admin")) return navigate("/admin/login");
      void fetchEvents();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, period]);

  const fetchEvents = async () => {
    setLoading(true);
    let query = supabase
      .from("install_prompt_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(2000);

    const days = PERIODS[period].days;
    if (days != null) {
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      query = query.gte("created_at", since);
    }
    const { data, error } = await query;
    if (!error) setEvents((data ?? []) as EventRow[]);
    setLoading(false);
  };

  const totals = useMemo(() => {
    const t = { shown: 0, accepted: 0, dismissed: 0, failed: 0 };
    for (const e of events) t[e.event_type]++;
    return t;
  }, [events]);

  const conversion = totals.shown > 0
    ? ((totals.accepted / totals.shown) * 100).toFixed(1)
    : "0.0";

  const byPlatform = useMemo(() => {
    const map: Record<string, { shown: number; accepted: number; dismissed: number; failed: number }> = {};
    for (const e of events) {
      const p = e.platform ?? "other";
      map[p] ||= { shown: 0, accepted: 0, dismissed: 0, failed: 0 };
      map[p][e.event_type]++;
    }
    return Object.entries(map).map(([platform, v]) => ({
      platform,
      ...v,
      conversion: v.shown > 0 ? ((v.accepted / v.shown) * 100).toFixed(1) : "0.0",
    }));
  }, [events]);

  const byTrigger = useMemo(() => {
    // Trigger lives in details.trigger ('auto' | 'fab') for shown events.
    const map: Record<string, { shown: number; accepted: number }> = {};
    for (const e of events) {
      if (e.event_type !== "shown") continue;
      const trigger = (e.details as { trigger?: string } | null)?.trigger ?? "unknown";
      map[trigger] ||= { shown: 0, accepted: 0 };
      map[trigger].shown++;
    }
    // Approximate accepted-by-trigger by counting accepted events nearby is non-trivial;
    // attribute accepted to the most recent prior 'shown' per session is overkill here.
    // Instead show shown counts only and overall conversion as reference.
    return Object.entries(map).map(([trigger, v]) => ({ trigger, ...v }));
  }, [events]);

  const platformBadge = (p: string) => {
    const variant = p === "ios" ? "default" : p === "android" ? "secondary" : "outline";
    return <Badge variant={variant}>{p}</Badge>;
  };

  const eventBadge = (t: EventType) => {
    const map: Record<EventType, { variant: "default" | "secondary" | "outline" | "destructive"; icon: JSX.Element }> = {
      shown: { variant: "outline", icon: <Eye className="h-3 w-3" /> },
      accepted: { variant: "default", icon: <CheckCircle2 className="h-3 w-3" /> },
      dismissed: { variant: "secondary", icon: <XCircle className="h-3 w-3" /> },
      failed: { variant: "destructive", icon: <AlertTriangle className="h-3 w-3" /> },
    };
    const m = map[t];
    return (
      <Badge variant={m.variant} className="gap-1">
        {m.icon}
        {t}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Download className="h-7 w-7" />
              Install Prompt Analytics
            </h1>
            <p className="text-muted-foreground">
              Conversion of the mobile "Add to Home Screen" prompt
            </p>
          </div>
          <Select value={period} onValueChange={(v) => setPeriod(v as PeriodKey)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PERIODS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {[
            { label: "Shown", value: totals.shown, icon: Eye },
            { label: "Accepted", value: totals.accepted, icon: CheckCircle2 },
            { label: "Dismissed", value: totals.dismissed, icon: XCircle },
            { label: "Failed", value: totals.failed, icon: AlertTriangle },
            { label: "Conversion", value: `${conversion}%`, icon: Download },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.label}>
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium">{s.label}</CardTitle>
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{s.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>By Platform</CardTitle>
            <CardDescription>Shown vs. accepted broken down per platform</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Platform</TableHead>
                  <TableHead className="text-center">Shown</TableHead>
                  <TableHead className="text-center">Accepted</TableHead>
                  <TableHead className="text-center">Dismissed</TableHead>
                  <TableHead className="text-center">Failed</TableHead>
                  <TableHead className="text-center">Conversion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byPlatform.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No data</TableCell></TableRow>
                ) : byPlatform.map((r) => (
                  <TableRow key={r.platform}>
                    <TableCell>{platformBadge(r.platform)}</TableCell>
                    <TableCell className="text-center">{r.shown}</TableCell>
                    <TableCell className="text-center">{r.accepted}</TableCell>
                    <TableCell className="text-center">{r.dismissed}</TableCell>
                    <TableCell className="text-center">{r.failed}</TableCell>
                    <TableCell className="text-center font-semibold">{r.conversion}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>By Trigger</CardTitle>
            <CardDescription>How often each trigger surfaced the prompt</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trigger</TableHead>
                  <TableHead className="text-center">Shown</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byTrigger.length === 0 ? (
                  <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground">No data</TableCell></TableRow>
                ) : byTrigger.map((r) => (
                  <TableRow key={r.trigger}>
                    <TableCell><Badge variant="outline">{r.trigger}</Badge></TableCell>
                    <TableCell className="text-center">{r.shown}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Events</CardTitle>
            <CardDescription>Latest 100 events</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>
                  ) : events.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">No events</TableCell></TableRow>
                  ) : events.slice(0, 100).map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {format(new Date(e.created_at), "MMM d, HH:mm")}
                      </TableCell>
                      <TableCell>{eventBadge(e.event_type)}</TableCell>
                      <TableCell>{platformBadge(e.platform ?? "other")}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                        {e.details ? JSON.stringify(e.details) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default InstallAnalytics;
